import React, { useState } from 'react';
import { Space, Tooltip, Button, theme, Modal, Card, Typography, Input } from 'antd';
import {
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    FlagOutlined,
    ReloadOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text, TextArea } = Typography;

const MessageSpecialStates = ({
    message,
    isAbuse,
    isFailed,
    isEscalation,
    isDarkMode,
    onResolve,
    onRetry,
    resolving,
    retrying
}) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [resolveOpen, setResolveOpen] = useState(false);
    const [resolveText, setResolveText] = useState('');

    const warningColor = token.colorWarning;
    const errorColor = token.colorError;
    const iconSize = 14;

    const iconButtonStyle = {
        width: 24,
        height: 24,
        minWidth: 24,
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%'
    };

    const makeCircleStyle = (borderColor, background) => ({
        width: 24,
        height: 24,
        minWidth: 24,
        borderRadius: '50%',
        border: `1.5px solid ${borderColor}`,
        background: background || 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
    });

    const handleResolveSubmit = () => {
        onResolve(resolveText).then(() => {
            setResolveOpen(false);
            setResolveText('');
        });
    };

    if (!isAbuse && !isFailed && !isEscalation) return null;

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                <Space size={6} wrap>
                    {isAbuse && (
                        <Tooltip title={t('abusive_content')} placement="topRight">
                            <span style={makeCircleStyle(errorColor)}>
                                <ExclamationCircleOutlined style={{ fontSize: iconSize, color: errorColor }} />
                            </span>
                        </Tooltip>
                    )}

                    {isFailed && (
                        <Space size={4}>
                            <Tooltip title={t('failed')} placement="topRight">
                                <span style={makeCircleStyle(errorColor)}>
                                    <CloseCircleOutlined style={{ fontSize: iconSize, color: errorColor }} />
                                </span>
                            </Tooltip>
                            <Tooltip title={t('retry')} placement="top">
                                <Button
                                    size="small"
                                    type="primary"
                                    ghost
                                    shape="circle"
                                    icon={<ReloadOutlined style={{ fontSize: iconSize }} />}
                                    loading={retrying}
                                    onClick={(e) => { e.stopPropagation(); onRetry(); }}
                                    style={iconButtonStyle}
                                />
                            </Tooltip>
                        </Space>
                    )}

                    {isEscalation && (
                        <Space size={4}>
                            <Tooltip title={t('needs_escalation')} placement="topRight">
                                <span style={makeCircleStyle(warningColor, isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.02)')}>
                                    <FlagOutlined style={{ fontSize: iconSize, color: warningColor }} />
                                </span>
                            </Tooltip>
                            <Tooltip title={t('resolve')} placement="top">
                                <Button
                                    size="small"
                                    type="primary"
                                    shape="circle"
                                    icon={<CheckCircleOutlined style={{ fontSize: iconSize }} />}
                                    onClick={(e) => { e.stopPropagation(); setResolveOpen(true); }}
                                    style={iconButtonStyle}
                                />
                            </Tooltip>
                        </Space>
                    )}
                </Space>
            </div>

            <Modal
                title={t('resolve_escalation')}
                open={resolveOpen}
                onCancel={() => setResolveOpen(false)}
                onOk={handleResolveSubmit}
                okButtonProps={{ loading: resolving }}
                centered
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    {message?.content && (
                        <Card size="small">
                            <Text strong>{t('customer_query')}:</Text>
                            <div style={{ marginTop: 4 }}>
                                <Text style={{ whiteSpace: 'pre-wrap' }}>{message.content}</Text>
                            </div>
                        </Card>
                    )}
                    {message?.agent_analysis && (
                        <Card size="small">
                            <Text strong>{t('agent_analysis')}:</Text>
                            <div style={{ marginTop: 4 }}>
                                <Text style={{ whiteSpace: 'pre-wrap' }}>{message.agent_analysis}</Text>
                            </div>
                        </Card>
                    )}
                    <div>
                        <Text strong>{t('your_response')}</Text>
                        <TextArea
                            value={resolveText}
                            onChange={(e) => setResolveText(e.target.value)}
                            placeholder={t('type_your_response')}
                            rows={4}
                            style={{ marginTop: 8 }}
                        />
                    </div>
                </Space>
            </Modal>
        </>
    );
};

export default MessageSpecialStates;
